import { configureStore, createAction, createReducer, type EnhancedStore } from '@reduxjs/toolkit';
import type { ToastProps, ToastPropsBase } from '../../hooks/Toast/useToasts';

export type GlobalToastType = 'ui' | 'appInbox';

export type GlobalToastStoreType = {
  toasts: {
    uiToasts: ToastProps[];
    appInboxToasts: ToastPropsBase[];
  };
};
type GlobalToastsReduxState = {
  uiToasts: ToastProps[];
  appInboxToasts: ToastPropsBase[];
};

const initialState: GlobalToastsReduxState = {
  uiToasts: [],
  appInboxToasts: [],
};

export type AddActionPayload =
  | { type: 'ui'; value: ToastProps & { id: string } }
  | { type: 'appInbox'; value: ToastPropsBase };
// Redux Actions
const add = createAction<AddActionPayload>('toasts/add');
const remove = createAction<{ type: GlobalToastType; id: string }>('toasts/remove');
const clear = createAction<{ type: GlobalToastType }>('toasts/clear');

const globalToastsReducer = createReducer(initialState, builder => {
  builder.addCase(add, (state, action) => {
    if (action.payload.type === 'ui') {
      state.uiToasts.push(action.payload.value);
    } else {
      state.appInboxToasts.push(action.payload.value);
    }
  });
  builder.addCase(remove, (state, action) => {
    if (action.payload.type === 'ui') {
      state.uiToasts = state.uiToasts.filter(toast => toast.id !== action.payload.id);
    } else {
      state.appInboxToasts = state.appInboxToasts.filter(toast => toast.id !== action.payload.id);
    }
  });
  builder.addCase(clear, (state, action) => {
    if (action.payload.type === 'ui') {
      state.uiToasts = [];
    } else {
      state.appInboxToasts = [];
    }
  });
});

// Store solely for Toasts -
export const globalToastStore = configureStore<GlobalToastStoreType>({
  reducer: {
    toasts: globalToastsReducer,
  },
});

export type ReduxSelectorTypes<TStore extends EnhancedStore, TDispatch> = {
  storeType: TStore;
  dispatchType: TDispatch;
  selectorType: ReturnType<TStore['getState']>;
};

export type GlobalToastDispatch = typeof globalToastStore.dispatch;

export const globalToastsDispatch = globalToastStore.dispatch;
export const getGlobalToastsState = globalToastStore.getState;

export const globalToastActions = {
  addToast: add,
  remove,
  clear,
};
